import { InterfaceSettings } from "./components/InterfaceSettings";
import { NotificationsSettings } from "./components/NotificationsSettings";
import { ProfileForm, ProfileFormValues } from "./components/ProfileForm";
import { ProfileHeader } from "./components/ProfileHeader";
import { UserAuditLogs } from "./components/UserAuditLogs";
import { VerificationSettings } from "./components/VerificationSettings";
import { UserDto } from "@/common/api/datasources/Api";
import { useUsersQuery } from "@/common/api/services/users";
import { PageHeader } from "@/components/shared/PageHeader";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type { UseQueryResult } from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Bell, History, Loader2, Settings2, Shield, User } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { DeleteAccountSection } from "./components/DeleteAccountSection";

const UserProfile = () => {
  const { t } = useTranslation();
  const { userUUID } = useParams();

  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const usersQuery = useUsersQuery();
  const updateUserMutation = useMutation(usersQuery.updateUserMutation);

  const {
    data: user,
    isLoading: isUserLoading,
    error: userError,
    refetch,
  }: UseQueryResult<UserDto, Error> = useQuery({
    ...usersQuery.userByIdQuery(userUUID!),
    enabled: !!userUUID,
  });



  async function onSubmit(data: ProfileFormValues) {
    if (!userUUID) return;

    setIsLoading(true);
    try {
      await updateUserMutation.mutateAsync({
        userUUID,
        data: {
          ...data,
          firstName: data.firstName || undefined,
          lastName: data.lastName || undefined,
          birthDate: data.birthDate?.toISOString(),
          phoneNumber: data.phoneNumber,
        },
      });

      await refetch();

      const isEmailChanged = data.email !== user?.email;
      const isPhoneChanged = data.phoneNumber !== user?.phoneNumber;
      const isPhoneRemoved = data.phoneNumber === "" && user?.phoneNumber;

      if (isPhoneRemoved) {
        toast({
          title: t("profile.toast.success.title"),
          description: t("profile.toast.success.phoneRemoved"),
        });
      } else if (isEmailChanged && isPhoneChanged) {
        toast({
          title: t("profile.toast.success.title"),
          description: `${t("profile.toast.success.emailChanged")} ${t("profile.toast.success.phoneChanged")}`,
        });
      } else if (isEmailChanged) {
        toast({
          title: t("profile.toast.success.title"),
          description: t("profile.toast.success.emailChanged"),
        });
      } else if (isPhoneChanged) {
        toast({
          title: t("profile.toast.success.title"),
          description: t("profile.toast.success.phoneChanged"),
        });
      } else {
        toast({
          title: t("profile.toast.success.title"),
          description: t("profile.toast.success.description"),
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("profile.toast.error.title"),
        description: t("profile.toast.error.description"),
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleVerificationSuccess = () => {
    refetch();
  };

  if (isUserLoading) {
    return (
      <div className="flex h-[calc(100vh-var(--header-height))] items-center justify-center">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>{t("common.loading")}</span>
        </div>
      </div>
    );
  }

  if (userError) {
    return (
      <div className="flex h-[calc(100vh-var(--header-height))] items-center justify-center">
        <div className="text-destructive">
          {t("common.errors.loading")}
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex h-[calc(100vh-var(--header-height))] items-center justify-center">
        <div className="text-muted-foreground">
          {t("common.errors.notFound")}
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-var(--header-height))]">
      <div className="container mx-auto space-y-6 p-8">
        <PageHeader
          icon={<Settings2 className="h-6 w-6" />}
          title={t("settings.title")}
          description={t("settings.description")}
        />

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground w-full">
            <TabsTrigger
              value="profile"
              className={cn(
                "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm gap-2 flex-1",
              )}
            >
              <User className="h-4 w-4" />
              {t("settings.tabs.profile")}
            </TabsTrigger>
            <TabsTrigger
              value="general"
              className={cn(
                "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm gap-2 flex-1",
              )}
            >
              <Settings2 className="h-4 w-4" />
              {t("settings.tabs.general")}
            </TabsTrigger>

            <TabsTrigger
              value="notifications"
              className={cn(
                "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm gap-2 flex-1",
              )}
            >
              <Bell className="h-4 w-4" />
              {t("settings.tabs.notifications")}
            </TabsTrigger>
            <TabsTrigger
              value="audit"
              className={cn(
                "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm gap-2 flex-1",
              )}
            >
              <History className="h-4 w-4" />
              {t("settings.tabs.audit")}
            </TabsTrigger>
          </TabsList>

          <div className="mt-6 space-y-6">
            <TabsContent value="profile" className="space-y-6">
              <ProfileHeader user={user} />
              <Separator className="my-6" />
              <ProfileForm
                user={user}
                isLoading={isLoading}
                onSubmit={onSubmit}
              />
            </TabsContent>

            <TabsContent value="general" className="space-y-6">
              <InterfaceSettings />
              <VerificationSettings
                user={user}
                onVerificationSuccess={handleVerificationSuccess}
              />
              <DeleteAccountSection />
            </TabsContent>

            <TabsContent value="notifications" className="space-y-6">
              <NotificationsSettings />
            </TabsContent>
            <TabsContent value="audit" className="space-y-6">
              <UserAuditLogs user={user} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </ScrollArea>
  );
};

export default UserProfile;
